import { z } from 'zod'
import type { Rng } from '../rng.js'

/**
 * A single thing worth being able to do, tracked separately.
 *
 * Fact families rather than broad topics: "the seven times table" is something
 * you can be weak at while being fine at the fives, and lumping them together
 * would hide exactly the detail that makes practice useful.
 */
export interface Skill {
  readonly key: string
  /** Shown to a parent, not to the player. */
  readonly label: string
  /** The skill level at which this starts appearing. */
  readonly unlocksAt: number
  build(rng: Rng): Question
}

export interface Question {
  readonly skillKey: string
  /** What the player reads, e.g. "7 × 8". */
  readonly prompt: string
  readonly answer: number
}

export const MIN_LEVEL = 0
export const MAX_LEVEL = 10

/** Where somebody who has never played starts. */
export const STARTING_LEVEL = 2

const range = (rng: Rng, low: number, high: number): number => low + rng.int(high - low + 1)

function addition(digits: 1 | 2, unlocksAt: number): Skill {
  const [low, high] = digits === 1 ? [1, 9] : [10, 99]

  return {
    key: `add.${digits}-digit`,
    label: digits === 1 ? 'Adding single digits' : 'Adding two-digit numbers',
    unlocksAt,
    build(rng) {
      const a = range(rng, low, high)
      const b = range(rng, low, high)
      return { skillKey: `add.${digits}-digit`, prompt: `${a} + ${b}`, answer: a + b }
    },
  }
}

function subtraction(digits: 1 | 2, unlocksAt: number): Skill {
  const [low, high] = digits === 1 ? [1, 9] : [10, 99]

  return {
    key: `subtract.${digits}-digit`,
    label: digits === 1 ? 'Taking away single digits' : 'Taking away two-digit numbers',
    unlocksAt,
    build(rng) {
      const a = range(rng, low, high)
      const b = range(rng, low, high)
      // Larger first, so the answer is never negative. Negative numbers are a
      // different lesson and not this one.
      const [big, small] = a >= b ? [a, b] : [b, a]
      return {
        skillKey: `subtract.${digits}-digit`,
        prompt: `${big} − ${small}`,
        answer: big - small,
      }
    },
  }
}

function timesTable(table: number, unlocksAt: number): Skill {
  return {
    key: `multiply.${table}`,
    label: `The ${table} times table`,
    unlocksAt,
    build(rng) {
      const other = range(rng, 1, 12)
      return { skillKey: `multiply.${table}`, prompt: `${table} × ${other}`, answer: table * other }
    },
  }
}

function division(table: number, unlocksAt: number): Skill {
  return {
    key: `divide.${table}`,
    label: `Dividing by ${table}`,
    unlocksAt,
    build(rng) {
      // Built from the multiplication, so it always divides exactly. A
      // remainder here would be a different lesson arriving unannounced.
      const other = range(rng, 1, 12)
      return {
        skillKey: `divide.${table}`,
        prompt: `${table * other} ÷ ${table}`,
        answer: other,
      }
    },
  }
}

const FRACTION_OF: Skill = {
  key: 'fraction.of-amount',
  label: 'Fractions of an amount',
  unlocksAt: 7,
  build(rng) {
    const denominator = rng.pick([2, 3, 4, 5, 10])
    const multiple = range(rng, 2, 12)
    const total = denominator * multiple
    return {
      skillKey: 'fraction.of-amount',
      prompt: `1/${denominator} of ${total}`,
      answer: multiple,
    }
  },
}

/**
 * Decimals, kept to whole answers.
 *
 * Reading and reasoning about a decimal is the lesson here. Making the answer
 * itself a decimal would mean a number pad with a point on it and a whole
 * class of "is 0.5 the same as .50" grading questions, which is a different
 * problem for a different day.
 */
const DECIMAL_OF: Skill = {
  key: 'decimal.of-amount',
  label: 'Decimal fractions of an amount',
  unlocksAt: 8,
  build(rng) {
    const decimal = rng.pick([0.5, 0.25, 0.1])
    const step = decimal === 0.25 ? 4 : decimal === 0.1 ? 10 : 2
    const total = step * range(rng, 2, 12)

    return {
      skillKey: 'decimal.of-amount',
      prompt: `${decimal} × ${total}`,
      answer: decimal * total,
    }
  },
}

const DECIMAL_ADD: Skill = {
  key: 'decimal.add',
  label: 'Adding decimals',
  unlocksAt: 8,
  build(rng) {
    // Halves that add to a whole, so the sum is countable rather than fiddly.
    const a = range(rng, 1, 20) + 0.5
    const b = range(rng, 1, 20) + 0.5

    return {
      skillKey: 'decimal.add',
      prompt: `${a} + ${b}`,
      answer: a + b,
    }
  },
}

const PERCENT_OF: Skill = {
  key: 'percent.of-amount',
  label: 'Percentages of an amount',
  unlocksAt: 9,
  build(rng) {
    const percent = rng.pick([10, 20, 25, 50])
    // Kept to whole answers: the arithmetic is the lesson, not the rounding.
    const total = range(rng, 1, 20) * (100 / percent)
    return {
      skillKey: 'percent.of-amount',
      prompt: `${percent}% of ${total}`,
      answer: (total * percent) / 100,
    }
  },
}

/**
 * Everything she might be asked, in roughly the order it is met at school.
 *
 * Ordered by when it unlocks rather than by topic, so the progression is
 * legible at a glance and adding a skill means picking a level.
 */
export const SKILLS: readonly Skill[] = [
  addition(1, 0),
  subtraction(1, 0),
  addition(2, 3),
  subtraction(2, 4),
  timesTable(2, 4),
  timesTable(5, 4),
  timesTable(10, 4),
  timesTable(3, 5),
  timesTable(4, 5),
  timesTable(6, 6),
  timesTable(7, 6),
  timesTable(8, 6),
  timesTable(9, 6),
  timesTable(11, 7),
  timesTable(12, 7),
  division(2, 6),
  division(5, 6),
  division(10, 6),
  division(3, 7),
  division(4, 7),
  FRACTION_OF,
  DECIMAL_OF,
  DECIMAL_ADD,
  PERCENT_OF,
]

export function skillByKey(key: string): Skill | undefined {
  return SKILLS.find((skill) => skill.key === key)
}

export type SkillLevels = Readonly<Record<string, number>>

/**
 * Levels as the server sends them.
 *
 * Parsed rather than assumed, like every other response the app reads. A key
 * this build does not know is a skill added or renamed since — kept rather
 * than refused, because dropping the whole response would take the ones it
 * does know with it, and `skillByKey` already ignores what it cannot match.
 */
export const skillLevelsSchema = z.record(z.string(), z.number().int())

export function levelOf(levels: SkillLevels, key: string): number {
  return levels[key] ?? STARTING_LEVEL
}

/** The best single number to describe how far along somebody is. */
export function overallLevel(levels: SkillLevels): number {
  const known = Object.values(levels)
  if (known.length === 0) return STARTING_LEVEL

  return known.reduce((total, level) => total + level, 0) / known.length
}
